import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { retrieveFromLocalStorage, getClassNames } from '../utils/util';
import { checkSession, endSession } from '../utils/session-timer';

import { requestViewPersonProfile } from '../api/party/party.actions';
import { requestWaitForEvents } from '../api/event/event.actions';
import { requestRetrieveShortcuts } from '../api/system-user/system-user.actions';
import { requestVerifyUser } from '../api/verification/verification.actions';
import { requestRetrieveAdminPings } from '../api/system-user/system-user.actions';

import SideMenu from '../components/common/side-menu/SideMenu';
import Shortcuts from '../components/common/shortcuts/Shortcuts';
import NotificationPanel from '../components/common/notification-panel/NotificationPanel';

import './layout.scss';

const Layout = ({ hideShortcuts }) => {
  const dispatch = useDispatch();

  const { profile, adminPings, userGroups } = useSelector(({ party }) => party);
  const { shortcuts } = useSelector(({ user }) => user);

  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [areNewNotifications, setAreNewNotifications] = useState(false);
  const [newNotificationsSize, setNewNotificationsSize] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const checkUserSession = () => {
      const sessionActive = checkSession();
      if (!sessionActive) {
        endSession();
        navigate('/');
      }
    };

    const sessionCheckInterval = setInterval(checkUserSession, 1000);

    return () => {
      clearInterval(sessionCheckInterval);
    };
  });

  useEffect(() => {
    if (!profile) {
      const personExternalReference = retrieveFromLocalStorage('personExternalReference');
      dispatch(requestViewPersonProfile({ personExternalReference }));
    }
  }, [dispatch, profile]);

  useEffect(() => {
    if (userGroups && userGroups.includes('admin')) {
      const personExternalReference = retrieveFromLocalStorage('personExternalReference');
      dispatch(requestRetrieveAdminPings({ personExternalReference }));
    }
  }, [dispatch, userGroups]);

  useEffect(() => {
    dispatch(requestVerifyUser({}));
  }, [dispatch]);

  useEffect(() => {
    if (!shortcuts || shortcuts.length === 0) {
      dispatch(requestRetrieveShortcuts());
    }
  }, [dispatch, shortcuts]);

  useEffect(() => {
    dispatch(requestWaitForEvents());
  }, [dispatch, profile]);

  const toggleNotifications = () => {
    setShowNotificationModal(!showNotificationModal);
  };

  return (
    <section className="layout">
      <SideMenu profile={profile} adminPings={adminPings} />
      <NotificationPanel
        showModal={showNotificationModal}
        setAreNewNotifications={setAreNewNotifications}
        setNewNotificationsSize={setNewNotificationsSize}
      />
      <section
        className="layout__content-wrapper"
        onClick={() => {
          if (showNotificationModal) {
            setShowNotificationModal(false);
          }
        }}
      >
        {!hideShortcuts && shortcuts && (
          <Shortcuts
            shortcuts={shortcuts}
            toggleNotifications={toggleNotifications}
            areNewNotifications={areNewNotifications}
            newNotificationsSize={newNotificationsSize}
          />
        )}
        <section className={getClassNames('layout__content', { 'hide-shortcuts': hideShortcuts || !shortcuts })}>
          <Outlet />
        </section>
      </section>
    </section>
  );
};

Layout.propTypes = {
  hideShortcuts: PropTypes.bool,
};

Layout.defaultProps = {
  hideShortcuts: false,
};

export default Layout;
